import { useEffect } from 'react';
import { useAudioStore } from '@/stores/audioStore';
import { WORLD_NAME_BY_SLUG } from '@/components/audio/shared/helpers';

const ACTION_HANDLERS: readonly MediaSessionAction[] = [
  'play',
  'pause',
  'seekto',
  'seekbackward',
  'seekforward',
  'previoustrack',
  'nexttrack',
] as const;

/**
 * Wire the Media Session API to audioStore state. Re-sets metadata on
 * story/title/childName/worldSlug changes; routes lock-screen and system-
 * media-controls events (play/pause/seek/skip) through audioStore
 * actions so iOS Safari, Android Chrome, and the macOS Now-Playing
 * widget all display proper track info and control the same underlying
 * `<audio>` element.
 *
 * Skip-15 rides on both `seekbackward`/`seekforward` (semantically
 * correct) and `previoustrack`/`nexttrack` (lock-screen convention —
 * iOS Safari renders prev/next buttons when those handlers are
 * registered, which users expect to skip within the track at a fixed
 * interval).
 *
 * Call once at the AppShell level. Feature-detects `mediaSession` and
 * no-ops on unsupported browsers (older Safari, Firefox on some
 * platforms) — primary PWA audio still works there via the standard
 * `<audio>` element; only the enhanced lock-screen surface is absent.
 */
export function useMediaSession() {
  const storyId = useAudioStore((s) => s.storyId);
  const title = useAudioStore((s) => s.title);
  const childName = useAudioStore((s) => s.childName);
  const worldSlug = useAudioStore((s) => s.worldSlug);

  useEffect(() => {
    if (!('mediaSession' in navigator)) return;

    if (!storyId || !title) {
      navigator.mediaSession.metadata = null;
      return;
    }

    navigator.mediaSession.metadata = new MediaMetadata({
      title,
      artist: childName ? `für ${childName}` : 'Dreamable',
      album: worldSlug ? WORLD_NAME_BY_SLUG[worldSlug] : 'Dreamable',
      // PNGs are listed FIRST because iOS Safari picks the first artwork
      // entry it can render and does NOT fall through to later entries on
      // silent failure (iOS 18+ SVG sanitization). SVG kept as last
      // fallback for platforms that explicitly prefer vector (some macOS
      // Now-Playing, legacy Android). The brand wordmark on the Now-
      // Playing cover (Batch 6.2.3) keeps cover + lock-screen artwork in
      // sync — users see the same mark on both surfaces.
      artwork: [
        { src: '/icons/icon-512.png', sizes: '512x512', type: 'image/png' },
        { src: '/icons/icon-192.png', sizes: '192x192', type: 'image/png' },
        { src: '/logo-dreamable.svg', sizes: 'any', type: 'image/svg+xml' },
      ],
    });

    navigator.mediaSession.setActionHandler('play', () => {
      void useAudioStore.getState().play();
    });
    navigator.mediaSession.setActionHandler('pause', () => {
      useAudioStore.getState().pause();
    });
    navigator.mediaSession.setActionHandler('seekto', (ev) => {
      if (typeof ev.seekTime === 'number') {
        useAudioStore.getState().seek(ev.seekTime);
      }
    });
    navigator.mediaSession.setActionHandler('seekbackward', () => {
      useAudioStore.getState().skipBy(-15);
    });
    navigator.mediaSession.setActionHandler('seekforward', () => {
      useAudioStore.getState().skipBy(15);
    });
    navigator.mediaSession.setActionHandler('previoustrack', () => {
      useAudioStore.getState().skipBy(-15);
    });
    navigator.mediaSession.setActionHandler('nexttrack', () => {
      useAudioStore.getState().skipBy(15);
    });

    return () => {
      if (!('mediaSession' in navigator)) return;
      ACTION_HANDLERS.forEach((action) => {
        navigator.mediaSession.setActionHandler(action, null);
      });
    };
  }, [storyId, title, childName, worldSlug]);
}
